import { mixInClassedAds, MixInClassedAdsOpts } from 'data-source/helpers/mixInClassedAds'
import { headPropsMapper } from 'data-source/mappers/headPropsMapper'
import { metaMapper, MetaMapperOpts } from 'data-source/mappers/metaMapper'
import { pLinkMapper, PLinkMapperOpts } from 'data-source/mappers/pLinkMapper'
import { postSectionsMapper } from 'data-source/mappers/postSectionsMapper'
import { subscribeTagsMapper } from 'data-source/mappers/subscribeTagsMapper'
import { teamMemberMapper, TeamMemberMapperOpts } from 'data-source/mappers/teamMemberMapper'
import { ApiCommonPostPageProps, ApiComponent, ApiTooltipProps } from 'data-source/types.wp'
import { mixArrays } from 'shared-code/array'
import { nullableString } from 'shared-code/conversion'
import {
  DSNCAdClassed,
  DSNCMulti,
  DSNCString,
  DSPostPage,
  DSResource,
  DSTooltip,
} from 'shared-definitions/types'

interface PostPageMapperOpts<T extends DSResource, A extends ApiComponent>
  extends MetaMapperOpts,
    PLinkMapperOpts,
    MixInClassedAdsOpts,
    TeamMemberMapperOpts {
  pagePath: string
  firstDesktopAd?: 'video' | 'plain'
  ampPagePath: string | null
  componentItemMapper: (input: A) => T | T[]
}

type Ex<T extends DSResource> = T | DSNCMulti<T> | DSNCAdClassed | DSNCString

export function postPageMapper<T extends DSResource, A extends ApiComponent>(
  input: ApiCommonPostPageProps<A>,
  opts: PostPageMapperOpts<T, A>
): DSPostPage<Ex<T>> {
  function convertTooltips(i: Record<string, ApiTooltipProps>): Record<string, DSTooltip> {
    return Object.keys(i).reduce((carry, key) => {
      carry[key] = {
        title: i[key].title,
        html: i[key].content,
      }
      return carry
    }, {} as Record<string, DSTooltip>)
  }

  // to mix ads in post body
  const inlineAds = input.in_article_ad?.flatMap(opts.componentItemMapper) ?? []

  // should be inserted before second h2
  const inlineAds2 = input.in_article_ad_2?.flatMap(opts.componentItemMapper) ?? []

  const sidebarAds = input.sidebar_ad?.flatMap(opts.componentItemMapper) ?? []
  const blocks = insertBeforeSecondH2(
    input.content.content.flatMap(opts.componentItemMapper) as Ex<T>[],
    inlineAds2
  )

  const category = input.categories.length ? input.categories[0] : null
  return {
    tooltips:
      input.tooltips && !Array.isArray(input.tooltips) ? convertTooltips(input.tooltips) : {},
    ampPagePath: opts.ampPagePath,
    pagePath: opts.pagePath,
    sideMenu:
      input.siblings?.map(sibling => ({
        label: sibling.name,
        pLink: opts.postLinkBuilder(sibling.slug),
        active: sibling.slug === category?.slug,
      })) ?? [],
    showAffilate: input.show_affiliate ?? true, // default - show affilate
    resource: 'post-page',
    startsWithImage: input.content.starts_with_image ?? false,
    head: headPropsMapper(input.head),
    title: input.content.title,
    subtitle: nullableString(input.content.subtitle),
    breadcrumbs: input.breadcrumbs
      ? input.breadcrumbs.map(i => ({
          label: i.name,
          pLink: pLinkMapper(i.slug, opts),
        }))
      : [],
    blocks: mixInClassedAds(
      mixArrays(
        blocks,
        [
          {
            margin: 0.1,
            arr: [{ resource: 'nc-multi', blocks: sidebarAds, visible: 'mobile' }],
          },
          {
            margin: 0.25,
            arr: [{ resource: 'nc-multi', blocks: inlineAds, visible: 'all' }],
          },
        ],
        (prev, next) => {
          if (prev.resource !== 'nc-string' || next.resource !== 'nc-string') {
            return false
          }
          return !/<h\d/gis.test((prev as DSNCString).html)
        }
      ),
      opts
    ),
    firstPublishedDate: nullableString(input.content.first_published),
    date: input.content.date,
    authors: {
      authoredBy: input.authors.map(i => teamMemberMapper(i, opts)),
      updatedBy: input.content_creators?.updated_by.map(i => teamMemberMapper(i, opts)) ?? [],
      reviewedBy: input.content_creators?.reviewed_by.map(i => teamMemberMapper(i, opts)) ?? [],
    },
    meta: metaMapper(input, subscribeTagsMapper(input), opts),
    sidebarContent: [],
    sidebarAds,
    underTitleAds: input.under_title_ad?.flatMap(opts.componentItemMapper) ?? [],
    sections: input.sections ? postSectionsMapper(input.sections) : null,
  }
}

function insertBeforeSecondH2<T extends DSResource>(blocks: Ex<T>[], ads: T[]): Ex<T>[] {
  if (!ads.length) {
    return blocks
  }

  let h2Counter = 0
  let insertBefore = blocks.length - 1
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]
    if (block.resource === 'nc-string') {
      if (/<h2/gis.test((block as DSNCString).html)) {
        h2Counter++
        if (h2Counter === 2) {
          insertBefore = i
          break
        }
      }
    }
  }

  return [...blocks.slice(0, insertBefore), ...ads, ...blocks.slice(insertBefore)]
}
